/**
 * cameraSeeThrough — fades environment Gaussian splats that sit between the
 * camera and the avatar head, so geometry doesn't occlude the face while
 * orbiting (camera-in-blueprint, see-through mode).
 *
 * Implemented as a Spark `worldModifier` (a per-Gaussian dyno) installed on the
 * environment SplatMesh. In a worldModifier the gsplat `center` is world-space
 * (post-transform), so we can measure its distance to the camera→head segment
 * directly and scale `opacity` down inside a radius. Per-frame the camera + head
 * world positions are pushed into two dynoVec3 uniforms — a pure uniform upload,
 * no shader recompile and no re-sort (Spark sorts by depth, not opacity).
 *
 * Ownership: the Spark dyno lives HERE, never in cameraController — the camera
 * singleton only forwards {camPos, headPos} each frame via a registered sink.
 * SplatEnvironment installs/tears this down when tune.camera.seeThrough changes.
 *
 * Loud failure (CLAUDE.md UX rule): if the modifier can't be built/installed we
 * warn and leave the splat untouched — never a silently faked effect.
 */

import * as THREE from 'three'
import { dyno } from '@sparkjsdev/spark'

const D = dyno
const { dynoBlock, Dyno, Gsplat, splitGsplat, combineGsplat, dynoVec3, dynoFloat } = D

class CameraSeeThrough {
  constructor() {
    this._mesh = null
    this._config = null
    // Whether a modifier is currently installed. Tracked here because Spark's
    // `mesh.worldModifier` is a WRITE-ONLY setter (it stores into
    // `mesh.worldModifiers[]`; there's no getter), so reading it back is always
    // undefined — update() can't use it to decide whether to force a regenerate.
    this._active = false
    this._camPos = dynoVec3(new THREE.Vector3())
    this._headPos = dynoVec3(new THREE.Vector3())
    // Global fade strength (0 = splat untouched, 1 = full fade). Driven per-frame
    // by cameraController — 1 for manual see-through, or an eased 0↔1 for `auto`
    // (proximity/angle heuristic). A uniform, so no rebuild when it changes.
    this._strength = dynoFloat(1)
    this._report('idle')
  }

  /**
   * Install / refresh the modifier on a SplatMesh. Rebuilds when the config
   * changes (radius/fade bake into the GLSL); the per-frame hot path only
   * mutates the camPos/headPos uniforms so this is called rarely.
   * @param {import('@sparkjsdev/spark').SplatMesh} mesh
   * @param {{enabled:boolean, radius:number, fade:number}} config
   */
  install(mesh, config) {
    if (!mesh) return
    this._mesh = mesh
    this._config = config
    try {
      // Install whenever manual OR auto is on — auto drives the fade via the
      // strength uniform, so the modifier must be resident for it to work.
      if (!config?.enabled && !config?.auto) {
        mesh.worldModifier = undefined
        mesh.updateGenerator()
        this._active = false
        this._report('idle')
        return
      }
      mesh.worldModifier = this._buildModifier(config)
      mesh.updateGenerator()
      this._active = true
      this._report('applied')
    } catch (e) {
      console.warn('[cameraSeeThrough] worldModifier install failed — see-through disabled:', e?.message ?? e)
      this._active = false
      this._report('error')
    }
  }

  /** Remove the modifier and forget the mesh (env swap / teardown). */
  uninstall() {
    const mesh = this._mesh
    if (mesh) {
      try { mesh.worldModifier = undefined; mesh.updateGenerator() } catch { /* mesh disposed */ }
    }
    this._mesh = null
    this._config = null
    this._active = false
    this._report('idle')
  }

  /** Per-frame camera + head world positions (arrays [x,y,z]) + fade strength
   *  (0..1). Cheap uniform upload; no-op if never installed. */
  update(camPos, headPos, strength) {
    if (camPos) this._camPos.value.set(camPos[0], camPos[1], camPos[2])
    let changed = false
    if (headPos) {
      const h = this._headPos.value
      if (Math.abs(h.x - headPos[0]) > 1e-4 || Math.abs(h.y - headPos[1]) > 1e-4 || Math.abs(h.z - headPos[2]) > 1e-4) changed = true
      h.set(headPos[0], headPos[1], headPos[2])
    }
    if (strength !== undefined && strength !== null) {
      if (Math.abs(this._strength.value - strength) > 1e-4) changed = true
      this._strength.value = strength
    }
    // Spark regenerates a splat's modified attributes only when the CAMERA view
    // changes (its viewChanged test) or the mesh version bumps — a bare uniform
    // upload doesn't re-run the generator. So when the head/strength move while
    // the camera is static (e.g. the avatar walks past), the fade would freeze
    // at the last camera move. Bump the mesh version to force a regenerate that
    // re-reads these uniforms. This is NOT updateGenerator() (that recompiles
    // the dyno graph); needsUpdate just re-runs the existing generator program.
    // Guarded on an installed modifier so a disabled see-through never churns.
    if (changed && this._active && this._mesh) this._mesh.needsUpdate = true
  }

  _buildModifier(cfg) {
    const camPos = this._camPos
    const headPos = this._headPos
    const strength = this._strength
    const radius = Math.max(1e-3, Number(cfg.radius ?? 0.6))
    const fade = Math.max(1e-3, Number(cfg.fade ?? 0.3))
    // Constant-radius cylinder around the camera→head segment.
    const glsl = `
      float _seeThroughFade(vec3 p, vec3 a, vec3 b) {
        vec3 ab = b - a;
        float denom = max(dot(ab, ab), 1e-6);
        float t = clamp(dot(p - a, ab) / denom, 0.0, 1.0);
        vec3 proj = a + t * ab;
        float d = distance(p, proj);
        float rad = ${radius.toFixed(5)};
        // 0 inside the radius (fully faded) → 1 outside radius+fade (untouched)
        return smoothstep(rad, rad + ${fade.toFixed(5)}, d);
      }`

    return dynoBlock(
      { gsplat: Gsplat },
      { gsplat: Gsplat },
      ({ gsplat }) => {
        const s = splitGsplat(gsplat).outputs
        const fadeNode = new Dyno({
          inTypes: { center: 'vec3', opacity: 'float', camPos: 'vec3', headPos: 'vec3', strength: 'float' },
          outTypes: { opacity: 'float' },
          inputs: { center: s.center, opacity: s.opacity, camPos, headPos, strength },
          globals: () => [glsl],
          // strength blends between untouched (1.0) and the computed fade, so
          // auto mode can ramp the effect in/out without a shader rebuild.
          statements: ({ inputs, outputs }) => [
            `${outputs.opacity} = ${inputs.opacity} * mix(1.0, _seeThroughFade(${inputs.center}, ${inputs.camPos}, ${inputs.headPos}), ${inputs.strength});`,
          ],
        })
        return { gsplat: combineGsplat({ gsplat, opacity: fadeNode.outputs.opacity }) }
      },
    )
  }

  _report(state) {
    if (typeof window !== 'undefined') {
      const c = this._config
      window.__CAMERA_SEE_THROUGH__ = {
        state,
        enabled: !!c?.enabled,
        auto: !!c?.auto,
        radius: c?.radius ?? null,
        fade: c?.fade ?? null,
      }
    }
  }
}

export const cameraSeeThrough = new CameraSeeThrough()

// Debug/test handle — drive update() directly and inspect the installed mesh
// without going through the render loop (diagnosing the follow/regenerate path).
if (typeof window !== 'undefined') {
  window.__CAMERA_SEE_THROUGH_CTL__ = cameraSeeThrough
}
